import Api from '@/data/Api.ts';
import StringUtils from '@/infrastructure/support/utils/StringUtils.ts';
import PlantApi from '@/data/plants/PlantApi.ts';
import _ from 'lodash';
import { addDays, format, isBefore } from 'date-fns';
import { useMeterStatusTypeStore } from '@/stores/MeterStatusTypeStore.ts';

const URL_METER_STATUSES_FOR_METER =
  '/companies/{0}/plants/{1}/meters/{2}/meter-statuses';
const URL_METER_STATUS_TYPES = '/meter-status-types';

// Module-level so concurrent callers share one request even though each
// caller constructs its own MeterStatusApi instance.
let pendingTypesRequest: Promise<any> | null = null;

export default class MeterStatusApi extends Api {
  meterStatusesForPlant(
    companyId: number,
    plantId: number,
    dateStart: string,
    dateEnd: string,
  ): Promise<any> {
    return this.getStatusesFromCorela(companyId, plantId, dateStart, dateEnd);
  }

  meterStatusesForMeter(
    companyId: number,
    plantId: number,
    meterId: number,
    dateStart: string,
    dateEnd: string,
  ): Promise<any> {
    return this.standardGet(
      StringUtils.stringFormat(URL_METER_STATUSES_FOR_METER, [
        companyId,
        plantId,
        meterId,
      ]),
      this.asParams(dateStart, dateEnd),
      [],
    );
  }

  async meterStatusesForPlantAsync(
    companyId: number,
    plantId: number,
    dateStart: string,
    dateEnd: string,
  ): Promise<any> {
    const meterIds = await new PlantApi()
      .getSinglePlantMeters(companyId, plantId)
      .then((rsp) => rsp.data.map((m) => m.id));

    // await avoids having many inflight request
    // which doesn't play nice with Valet

    let meterStatuses = [];
    for (let meterId of meterIds) {
      const newMs = await this.meterStatusesForMeter(
        companyId,
        plantId,
        meterId,
        dateStart,
        dateEnd,
      ).then((rsp) => rsp.data.data);
      meterStatuses = meterStatuses.concat(newMs);
    }
    return meterStatuses;
  }

  asParams(dateStart: string, dateEnd: string): {} {
    let params = {};
    if (dateStart) params['start_date'] = dateStart;
    if (dateEnd) params['end_date'] = dateEnd;
    return params;
  }

  getAllTypes(): Promise<any> {
    const store = useMeterStatusTypeStore();
    if (store.isFresh()) {
      return Promise.resolve({ data: { data: store.types } });
    }
    if (pendingTypesRequest) {
      return pendingTypesRequest;
    }

    pendingTypesRequest = this.standardGet(URL_METER_STATUS_TYPES, {}, [])
      .then((rsp) => {
        store.set(rsp.data.data);
        return rsp;
      })
      .finally(() => {
        pendingTypesRequest = null;
      });
    return pendingTypesRequest;
  }

  getStatusesFromCorela(
    companyId: number,
    plantId: number,
    dateStart: string,
    dateEnd: string,
  ): Promise<{}> {
    const statusesPromise: Promise<any> = new Promise((resolve) => {
      resolve(
        this.meterStatusesForPlantAsync(companyId, plantId, dateStart, dateEnd),
      );
    });

    const typesPromise: Promise<any> = this.getAllTypes() //
      .then((rsp) => rsp.data.data);

    return Promise.all([statusesPromise, typesPromise])
      .then((values) => this.enhanceMeterStatusesWithType(values[0], values[1]))
      .then((statuses) => _.groupBy(statuses, (mStatus) => mStatus.date))
      .then((statuses) =>
        this.fillEmptyDays(statuses, new Date(dateStart), new Date(dateEnd)),
      )
      .then((statuses) => this.sortObjectsByKeys(statuses));
  }

  enhanceMeterStatusesWithType(statuses, types) {
    types = _.keyBy(types, (msType) => msType.name);

    return statuses.map((mStatus) => {
      mStatus.meter_status_type_name = mStatus.type_name;
      mStatus.meter_status_type = types[mStatus.type_name];
      return mStatus;
    });
  }

  sortObjectsByKeys(statuses) {
    return Object.keys(statuses)
      .sort()
      .reduce((obj, key) => {
        obj[key] = statuses[key];
        return obj;
      }, {});
  }

  fillEmptyDays(statuses, start: Date, end: Date) {
    while (isBefore(start, end)) {
      const dateString = format(start, 'yyyy-MM-dd');
      if (statuses[dateString] === undefined) {
        statuses[dateString] = [];
      }

      start = addDays(start, 1);
    }
    return statuses;
  }
}
